/**
 * 자막 크기 단계 — 사용자가 +/− 로 직접 고른다(§14).
 *
 * 예전에는 기본/발표/발표XL 세 프리셋이었다. 프리셋은 두 가지가 불편했다:
 * 회의실마다 필요한 크기가 다른데 선택지가 셋뿐이었고, 프리셋마다 원문/번역
 * 비율이 제각각이라(0.60 / 0.50 / 0.40) 키울수록 위계가 흐트러졌다.
 *
 * **이 파일이 크기 규칙의 단일 정본이다.** CSS 도 테스트도 여기서 나온
 * 값을 쓴다 — 두 곳에 숫자를 적으면 반드시 갈라진다(실제로 갈라졌다).
 *
 * ── 불변식(caption-scale.test.ts 가 **모든 단계**에 대해 못박는다) ────
 *  1. 원문(src)은 번역(ko)보다 **뚜렷하게 작다** — 비율 ≤ 0.62
 *  2. 그 비율이 단계마다 **흔들리지 않는다** — 확대해도 위계가 유지된다
 *  3. 커질수록 원문이 **읽을 수 없게 되지는 않는다** — 절대 하한을 지킨다
 *     ("작게" 가 "안 보이게" 가 되면 요청의 취지를 넘어선다)
 *  4. 전사(passthrough) 카드는 통역 카드보다 **한 단계 작다**(§14.1)
 *     — 통역이 본론이고 전사는 기록이다. 단, 원문 줄보다는 크다.
 */

/** 단계 수 — 0(가장 작게) … 8(가장 크게). 기본은 2(예전 '기본' 크기). */
export const MIN_STEP = 0
export const MAX_STEP = 8
export const DEFAULT_STEP = 2
export const STEP_COUNT = MAX_STEP - MIN_STEP + 1

/**
 * 이 단계부터는 "발표" 로 본다 — 카드 간격을 넓히고 뷰어에서 원문 줄을
 * 접는다. 예전 '발표 모드' 프리셋(36px)이 있던 자리다.
 *
 * 크기와 레이아웃을 한 축으로 묶는 이유: 큰 글씨는 멀리서 보는 상황이고,
 * 그때는 번역 한 줄이 주인공이다. 별도 스위치로 두면 "글씨는 큰데 빽빽한"
 * 조합이 생긴다.
 */
export const PRESENTATION_STEP = 5

/**
 * 번역(ko) 본문 크기(rem) — 화면의 주인공이다.
 *
 * 등비에 가깝게 두되 실제 렌더 크기가 어중간하지 않도록 손으로 다듬었다.
 * 2단계(1.25rem=20px)가 예전 '기본', 5단계(2.25rem=36px)가 예전 '발표',
 * 7단계(3.75rem=60px)가 예전 '발표XL' 에 해당한다 — 기존 사용자가 쓰던
 * 크기가 사다리 위에 그대로 있다.
 */
const KO_REM = [0.875, 1.0, 1.25, 1.5, 1.875, 2.25, 3.0, 3.75, 4.5] as const

/**
 * 원문(src) 대 번역 비율 — **모든 단계에서 같다**(0.55).
 *
 * 예전에는 단계마다 0.60/0.50/0.40 으로 달랐다. 하나로 고정하면 어느 단계로
 * 가도 눈이 같은 위계를 본다. 다만 아래 하한이 이 비율보다 우선한다(불변식 3).
 */
export const SRC_RATIO = 0.55

/**
 * 원문 절대 하한(rem) — 큰 단계에서 원문이 사라지지 않게.
 *
 * 회의실 뒤쪽에서도 원문이 읽혀야 한다. 비율만 좇으면 큰 단계에서 원문이
 * 상대적으로만 작아지는 게 아니라 실제로 못 읽는 크기가 된다.
 */
const SRC_MIN_REM = [0.5, 0.5625, 0.75, 0.875, 1.0, 1.125, 1.25, 1.5, 1.75] as const

/** 전사(passthrough) 카드 본문 = 번역보다 한 단계 작게(§14.1). */
export const PASSTHROUGH_RATIO = 0.8

/** 메타(시각·배지) — 원문보다 크지 않다. */
export const META_RATIO = 0.5
const META_MIN_REM = 0.5

/* ── 카드 여백 — 글자 크기에서 판다 ───────────────────────────────────────
 *
 * 계수를 **작게** 잡는다. 여백은 한 화면에 담기는 발화 수를 줄이고, 그 수가
 * 곧 회의 맥락이다. 하한은 아주 작은 단계에서 카드끼리 들러붙어 경계가
 * 사라지는 것을 막는 최소치다(0.25rem = 4px).
 *
 * 좌우가 상하보다 계수가 큰 것은 의도다. 좌우 여백은 카드 **높이에 전혀
 * 기여하지 않으면서** 글줄이 테두리에 닿는 것만 막는다 — 줄일 이유가 없는
 * 쪽이라 읽기 편한 값을 준다. 높이를 만드는 것은 상하뿐이므로 거기만 조인다.
 */
export const PAD_Y_RATIO = 0.24
const PAD_Y_MIN_REM = 0.25
export const PAD_X_RATIO = 0.4
const PAD_X_MIN_REM = 0.5
/** 메타줄 ↔ 본문 — 한 덩어리로 읽히도록 아주 좁게. */
export const GAP_RATIO = 0.14
const GAP_MIN_REM = 0.125

/**
 * 전사(passthrough) 카드의 여백 배율 — 통역 카드보다 **더 조인다**(§14.1).
 *
 * 크기 위계(PASSTHROUGH_RATIO=0.8)와 **같은 결의 규칙**을 여백에도 준다.
 * 글자만 줄이고 여백을 그대로 두면 빈 카드가 오히려 커 보여 시선을 끈다 —
 * "덜 중요하다" 가 한눈에 읽히려면 크기와 여백이 함께 줄어야 한다.
 *
 * 별도 변수 집합(--caption-pt-pad-*)이 아니라 **배율 하나**로 두는 이유:
 * 두 벌을 따로 유지하면 한쪽만 고쳐 위계가 조용히 뒤집힌다(예전 프리셋
 * 비율 0.60/0.50/0.40 이 정확히 그렇게 갈라졌다). 배율이면 통역 카드를
 * 어떻게 바꾸든 전사 카드가 **항상 더 조밀하다** 는 관계가 산술로 보장된다.
 *
 * 배율은 크기 위계(0.8)보다 세게 잡는다. 여백은 글자와 달리 가독 하한이
 * 없어서 더 깎아도 잃는 것이 없고, 전사 카드는 원문 줄이 없어(§6.5) 애초에
 * 본문이 한 줄뿐이라 여백 비중이 상대적으로 크기 때문이다.
 */
export const PASSTHROUGH_PAD_RATIO = 0.7

export interface CaptionScale {
  step: number
  /** 번역 본문(rem) */
  ko: number
  /** 원문('들린 내용') 본문(rem) */
  src: number
  /** 전사 카드 본문(rem) */
  passthrough: number
  /** 메타(시각·배지) */
  meta: number
  /** 줄 간격 — 커질수록 촘촘하게(큰 글씨는 여유 줄간격이 오히려 성기다). */
  leading: number
  /** 카드 상하 여백(rem) — 글자에서 파생. */
  padY: number
  /** 카드 좌우 여백(rem) */
  padX: number
  /** 메타줄 ↔ 본문 간격(rem) */
  gap: number
  /** 전사 카드 상하 여백(rem) — 통역 카드보다 항상 좁다(§14.1). */
  ptPadY: number
  /** 전사 카드 좌우 여백(rem) */
  ptPadX: number
  /** 전사 카드 메타줄 ↔ 본문 간격(rem) */
  ptGap: number
}

function round(rem: number): number {
  // 0.0625rem = 1px 격자에 맞춘다 — 서브픽셀 크기는 렌더러마다 다르게 깨진다.
  return Math.round(rem * 16) / 16
}

export function clampStep(step: number): number {
  if (!Number.isFinite(step)) return DEFAULT_STEP
  return Math.min(MAX_STEP, Math.max(MIN_STEP, Math.round(step)))
}

/** 한 단계의 실제 크기들 — CSS 생성과 테스트가 **같은** 이 함수를 쓴다. */
export function scaleAt(step: number): CaptionScale {
  const i = clampStep(step)
  const ko = KO_REM[i]!
  // 비율과 하한 중 **큰 쪽**을 쓴다 — 위계보다 가독이 먼저다(불변식 3).
  const src = round(Math.max(ko * SRC_RATIO, SRC_MIN_REM[i]!))
  const passthrough = round(ko * PASSTHROUGH_RATIO)
  // 메타는 원문보다 크지 않다(시각 위계: 번역 > 원문 ≥ 메타). 작은 단계에서
  // 하한끼리 부딪히면 원문 쪽을 따른다.
  const meta = Math.min(src, round(Math.max(ko * META_RATIO, META_MIN_REM)))
  // 작은 글씨는 넉넉히, 큰 글씨는 촘촘히.
  const leading = i <= 2 ? 1.45 : i <= 5 ? 1.35 : 1.3
  // ── 카드 여백도 글자에서 판다(2026-08-06 실사용: "카드 높이가 너무 높다",
  //    "동적 크기 조정되게 여유 마진만 작게") ──────────────────────────────
  //
  // 여백을 고정 클래스(px-4 py-3)로 두면 두 방향 모두에서 어긋난다 — 크게
  // 키우면 글자에 비해 답답하고, 작게 줄이면 여백만 남아 한 화면에 담기는
  // 발화 수가 줄어든다. **한 화면의 발화 수가 곧 회의 맥락**이므로 여백은
  // 그만큼의 맥락을 밀어내는 비용이다.
  //
  // 그래서 글자 크기에 비례시키되 **계수를 작게** 잡는다. 하한을 두는 이유는
  // 아주 작은 단계에서 카드끼리 들러붙어 경계가 사라지기 때문이다.
  const padY = round(Math.max(ko * PAD_Y_RATIO, PAD_Y_MIN_REM))
  const padX = round(Math.max(ko * PAD_X_RATIO, PAD_X_MIN_REM))
  // 메타줄과 본문 사이 — 둘은 한 덩어리로 읽히는 편이 낫다.
  const gap = round(Math.max(ko * GAP_RATIO, GAP_MIN_REM))
  // 전사 카드는 배율만큼 **더** 조인다(§14.1). 배율을 하한 **뒤에** 곱하는
  // 것이 요점이다 — 하한을 나중에 적용하면 작은 단계에서 두 카드가 똑같이
  // 하한에 걸려 위계가 조용히 사라진다. 여백에는 가독 하한이 없으므로
  // (글자와 달리) 더 깎아도 잃는 것이 없고, 1px 격자만 지키면 된다.
  const ptPadY = round(padY * PASSTHROUGH_PAD_RATIO)
  const ptPadX = round(padX * PASSTHROUGH_PAD_RATIO)
  const ptGap = round(gap * PASSTHROUGH_PAD_RATIO)
  return { step: i, ko: round(ko), src, passthrough, meta, leading,
           padY, padX, gap, ptPadY, ptPadX, ptGap }
}

export const ALL_STEPS: readonly number[] = Array.from(
  { length: STEP_COUNT },
  (_, i) => MIN_STEP + i,
)

/** 사람이 읽을 단계 표시 — "3 / 9". */
export function stepLabel(step: number): string {
  return `${clampStep(step) + 1} / ${STEP_COUNT}`
}

export const SCALE_STORAGE_KEY = 'omnitrans:caption-step'
